import React, { useEffect, useRef, useState } from 'react';
import Button from './Buttons';

/**
 * Shows an invite link with a Copy link button. Used when the Axaris installation does not send
 * email and the inviter has to pass the link on themselves.
 *
 * The link is a credential: it is held only in the caller's component state and is never written
 * to a toast, the page URL, browser storage or the console.
 *
 * Props:
 *   url     – the invite link
 *   compact – smaller layout for list rows (optional)
 */
const InviteLinkBox = ({ url, compact = false }) => {
    const inputRef = useRef(null);
    const timerRef = useRef(null);
    const [copied, setCopied] = useState(false);
    const [showManualHint, setShowManualHint] = useState(false);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    // Fallback: navigator.clipboard is missing on a non-secure origin (plain http on a LAN).
    const selectForManualCopy = () => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
        setShowManualHint(true);
    };

    const handleCopy = async () => {
        if (!navigator.clipboard?.writeText) {
            selectForManualCopy();
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            setShowManualHint(false);
            setCopied(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
        } catch {
            selectForManualCopy();
        }
    };

    if (!url) return null;

    return (
        <div className={compact ? 'mt-1' : 'mt-2'}>
            <div className="flex items-center gap-2">
                <input
                    ref={inputRef}
                    type="text"
                    readOnly
                    value={url}
                    aria-label="Invite link"
                    onFocus={(e) => e.target.select()}
                    className={`min-w-0 flex-1 rounded-md border border-gray-300 bg-white text-gray-900 font-mono ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}`}
                />
                <Button type="button" variant="secondary" size="sm" onClick={handleCopy}>
                    {copied ? 'Copied' : 'Copy link'}
                </Button>
            </div>
            {showManualHint && (
                <p className="mt-1 text-xs text-gray-600">Press Ctrl+C to copy</p>
            )}
        </div>
    );
};

export default InviteLinkBox;
